import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import api from '../../../lib/api';

const riskConfig = {
  high: { color: 'var(--color-error)', bg: '#FEE2E2', label: 'High Risk' },
  medium: { color: 'var(--color-warning)', bg: '#FEF3C7', label: 'Medium' },
  low: { color: 'var(--color-success)', bg: '#D1FAE5', label: 'Low Risk' },
};

const RenewalOpportunities = () => {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalValue, setTotalValue] = useState(0);

  useEffect(() => {
    const fetchRenewals = async () => {
      try {
        setLoading(true);
        const in10Days = new Date();
        in10Days?.setDate(in10Days?.getDate() + 10);
        const today = new Date()?.toISOString()?.split('T')?.[0];
        const in10DaysStr = in10Days?.toISOString()?.split('T')?.[0];

        const { data: allMembers } = await api.get('/members', { params: { limit: 200 } });

        const expiringMembers = (allMembers || [])
          ?.filter(m => m?.package_end_date && m?.package_end_date >= today && m?.package_end_date <= in10DaysStr)
          ?.sort((a, b) => a?.package_end_date?.localeCompare(b?.package_end_date))
          ?.slice(0, 5);

        const items = expiringMembers?.map(m => {
          const endDate = new Date(m?.package_end_date);
          const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
          const risk = daysLeft <= 3 ? 'high' : daysLeft <= 6 ? 'medium' : 'low';
          return {
            id: m?.id,
            name: `${m?.first_name || ''} ${m?.last_name || ''}`?.trim() || '—',
            plan: m?.package_name || 'Unknown Package',
            expiresIn: daysLeft,
            value: '$0',
            price: 0,
            reminderSent: false,
            risk,
          };
        });

        setRenewals(items);
        setTotalValue(items?.reduce((sum, r) => sum + r?.price, 0));
      } catch (err) {
        console.error('Error fetching renewals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRenewals();
  }, []);

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm lg:text-base font-semibold font-heading text-[var(--color-foreground)]">
          Upcoming Renewals
        </h3>
        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-caption font-medium">
          Next 10 days
        </span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <span className="text-xs text-[var(--color-muted-foreground)] font-caption">Loading renewals...</span>
        </div>
      ) : renewals?.length === 0 ? (
        <div className="text-center py-4 text-xs text-[var(--color-muted-foreground)] font-caption">
          No renewals due in the next 10 days.
        </div>
      ) : (
        <div className="space-y-2">
          {renewals?.map((r) => {
            const risk = riskConfig?.[r?.risk];
            return (
              <div
                key={r?.id}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold font-heading"
                  style={{ backgroundColor: `${risk?.color}18`, color: risk?.color }}
                >
                  {r?.expiresIn}d
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium font-caption text-[var(--color-foreground)] truncate">
                    {r?.name}
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-caption truncate">
                    {r?.plan} · {r?.value}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {r?.reminderSent ? (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-success)] font-caption">
                      <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
                      Sent
                    </span>
                  ) : (
                    <Button variant="outline" size="xs" iconName="Bell" iconPosition="left" iconSize={11}>
                      Remind
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between">
        <span className="text-xs text-[var(--color-muted-foreground)] font-caption">
          Total renewal value: <strong className="text-[var(--color-foreground)]">${totalValue?.toFixed(0)}</strong>
        </span>
        <Button variant="ghost" size="xs" iconName="ArrowRight" iconPosition="right" iconSize={12}>
          View All
        </Button>
      </div>
    </div>
  );
};

export default RenewalOpportunities;